import React from "react";
import {
  MonitorSmartphone,
  Ticket,
  LayoutGrid,
  Users,
  TrendingUp,
  Bell,
  CheckCircle2,
  ArrowRight,
  Play,
} from "lucide-react";
import HeaderMenu from "../../component/Navbar/headermenu";
import CustomFooter from "../../component/Footer/customFooter";
import ShowcaseImageLightbox from "../../component/ShowcaseImageLightbox";
import "./about.css";

import taskTable from "../../assets/IT/ittable.png";
import capsuleView from "../../assets/IT/itcapsule.png";
import calendarView from "../../assets/IT/itcalendar.png";
import peekView from "../../assets/IT/itpeekview.png";
import teamsView from "../../assets/IT/itteams.png";
import performance from "../../assets/IT/itperfomance.png";
import events from "../../assets/IT/itevents.png";
import reminders from "../../assets/IT/itreminders.png";
import leaveRequests from "../../assets/IT/itleaverequest.png";

const features = [
  {
    icon: <Ticket size={28} />,
    title: "Ticket & Project Tracking",
    description:
      "Every project, ticket, and client tracked as a task, with status, period, and financial year in one board.",
    badge: "Core",
    badgeColor: "#093a5b",
  },
  {
    icon: <LayoutGrid size={28} />,
    title: "Multiple Views",
    description:
      "Table, Capsule, or Calendar — switch between views for the same work without losing your filters.",
    badge: "Flexible",
    badgeColor: "#1a8fe3",
  },
  {
    icon: <Users size={28} />,
    title: "Team Visibility",
    description:
      "See every team member's workload, project, and status without asking for a status update.",
    badge: "Popular",
    badgeColor: "#10b981",
  },
  {
    icon: <TrendingUp size={28} />,
    title: "Performance Reporting",
    description:
      "Delivery rate, completion %, and on-time % per person, so you know who's on track before it's a problem.",
    badge: "Analytics",
    badgeColor: "#f59e0b",
  },
];

const trackingGroup = [
  {
    eyebrow: "Project & Ticket Tracking",
    title: "Every project, ticket, and client, in one board",
    description:
      "IoT Monitoring, Support Desk, Analytics Dashboard, School ERP, Hospital Management, and every other project tracked as tasks with client, status, period, and financial year — with To Do, In Progress, Under Review, and Closed counts always visible at the top.",
    image: taskTable,
    alt: "Simplifyte task table showing IT projects like IoT Monitoring, Support Desk, and Analytics Dashboard with client, status, and assignee columns",
  },
  {
    eyebrow: "Capsule View",
    title: "See the same work in the view that fits the moment",
    description:
      "Due date, expiry, client & group, assignees, and remarks laid out one card per task — switch to Capsule view anytime the table feels like too much at once.",
    image: capsuleView,
    alt: "Simplifyte Capsule View showing IT tasks as cards with client and group, due date, expiry, assignees, and remarks",
  },
  {
    eyebrow: "Calendar View",
    title: "Never lose track of what's due",
    description:
      "An overdue count you can't miss, today's due tasks listed alongside the calendar, and due/closed counts on every day of the month.",
    image: calendarView,
    alt: "Simplifyte Calendar View showing a monthly calendar with due and closed task counts, an overdue banner, and today's due tasks",
  },
  {
    eyebrow: "Ticket-level Detail",
    title: "Every ticket, with the full context engineers need",
    description:
      "Open any task to see its client, financial year, period, due date, assignee, and time log in a side peek panel — without losing your place in the list.",
    image: peekView,
    alt: "Simplifyte task peek panel open beside the task table, showing client, financial year, period, due date, and assignee",
  },
];

const teamGroup = [
  {
    eyebrow: "Team Visibility",
    title: "See what every team member is working on",
    description:
      "Switch to a teammate's view and see their full task list — project, client, period, and status — without pinging them for a status update.",
    image: teamsView,
    alt: "Simplifyte Teams view showing a team member's full task list with project, client, financial year, and status columns",
  },
  {
    eyebrow: "Performance Reporting",
    title: "Know who's on track, before it becomes a problem",
    description:
      "Delivery rate, tasks completed, and a per-person breakdown of completion % and on-time % — tagged On Track, Monitor, or Action Needed, so managers catch a slipping teammate early.",
    image: performance,
    alt: "Simplifyte Value Delivery Report showing delivery rate, tasks completed, and a per-user performance table with completion and on-time percentages",
  },
];

const scheduleGroup = [
  {
    eyebrow: "Events Calendar",
    title: "Sprints, releases, and client kickoffs, on one calendar",
    description:
      "Sprint planning, architecture reviews, client kickoffs, API workshops, QA cycles, and DevOps release windows — every team event visible by day, not buried in someone's personal calendar.",
    image: events,
    alt: "Simplifyte Events calendar showing sprint planning, architecture review, client kickoff, and DevOps release window entries",
  },
  {
    eyebrow: "Reminders",
    title: "The nudges that keep IT operations moving",
    description:
      "Overdue and due-today reminders — submitting a weekly timesheet, approving leave requests, updating project documentation — so routine admin work doesn't quietly slip.",
    image: reminders,
    alt: "Simplifyte Reminders screen showing overdue and pending reminders like submit weekly timesheet and approve leave requests",
  },
];

const officeGroup = [
  {
    eyebrow: "Leave & Presence Requests",
    title: "Request leave, track approval status, without leaving the platform",
    description:
      "Present and leave requests with pending, approved, and rejected counts, remarks, and who actioned each one — so engineers aren't following up over chat to know where their request stands.",
    image: leaveRequests,
    alt: "Simplifyte Office Hub Requests screen showing present and leave requests with status, created by, and action by columns",
  },
];

const benefits = [
  "Track every project, ticket, and client in one board",
  "Switch between Table, Capsule, and Calendar views without losing filters",
  "See team workload and performance without a standup meeting",
  "Approve leave and attendance requests without the chat back-and-forth",
  "Keep sprints, releases, and reminders visible to the whole team",
];

const renderGroup = (items, prefix) => (
  <div className="IT_Service_showcase_list">
    {items.map((item, index) => (
      <div
        key={`${prefix}-${index}`}
        className={`IT_Service_showcase_row ${
          index % 2 === 1 ? "IT_Service_showcase_row_reverse" : ""
        }`}
      >
        <div className="IT_Service_showcase_text">
          <span className="IT_Service_showcase_eyebrow">{item.eyebrow}</span>
          <h3 className="IT_Service_showcase_title">{item.title}</h3>
          <p className="IT_Service_showcase_description">
            {item.description}
          </p>
        </div>

        <div className="IT_Service_showcase_media">
          <div className="IT_Service_showcase_frame_outer">
            <div className="IT_Service_showcase_frame_inner">
              <ShowcaseImageLightbox
                src={item.image}
                alt={item.alt}
                className="IT_Service_showcase_image"
              />
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const ITServicesPage = () => {
  return (
    <>
      <HeaderMenu />
      <div className="IT_Service_main">
        {/* Hero */}
        <section className="IT_Service_hero">
          <div className="IT_Service_container">
            <div className="IT_Service_hero_wrapper">
              <div className="IT_Service_hero_content">
                <div className="IT_Service_badge">
                  <span className="IT_Service_badge_dot"></span>
                  Built for IT Teams
                </div>

                <h1 className="IT_Service_heading">
                  Workflow platform for{" "}
                  <span className="IT_Service_heading_highlight">
                    IT service teams
                  </span>
                </h1>

                <p className="IT_Service_subheading">
                  Track tickets, projects, and team workload with clear
                  visibility across the entire department. Streamline your IT
                  operations with intelligent workflows.
                </p>

                <div className="IT_Service_hero_cta">
                  <button
                    className="IT_Service_primary_button"
                    onClick={() =>
                      (window.location.href = "https://app.simplifyte.com")
                    }
                  >
                    Start Free Trial
                  </button>
                  <button className="IT_Service_secondary_button">
                    View Demo
                  </button>
                </div>

                <div className="IT_Service_hero_stats">
                  <div className="IT_Service_stat">
                    <div className="IT_Service_stat_number">10k+</div>
                    <div className="IT_Service_stat_label">IT Teams</div>
                  </div>
                  <div className="IT_Service_stat_divider"></div>
                  <div className="IT_Service_stat">
                    <div className="IT_Service_stat_number">99.9%</div>
                    <div className="IT_Service_stat_label">Uptime</div>
                  </div>
                  <div className="IT_Service_stat_divider"></div>
                  <div className="IT_Service_stat">
                    <div className="IT_Service_stat_number">24/7</div>
                    <div className="IT_Service_stat_label">Support</div>
                  </div>
                </div>
              </div>

              <div className="IT_Service_hero_visual">
                <div className="IT_Service_hero_image_wrapper">
                  <div className="IT_Service_hero_gradient"></div>
                  <img
                    src={taskTable}
                    alt="IT Dashboard Preview"
                    className="IT_Service_hero_image"
                  />
                  <div className="IT_Service_hero_floating_card">
                    <div className="IT_Service_floating_icon">
                      <CheckCircle2 size={20} />
                    </div>
                    <div className="IT_Service_floating_text">
                      <div className="IT_Service_floating_title">
                        Task Completed
                      </div>
                      <div className="IT_Service_floating_subtitle">
                        IoT Monitoring Project
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="IT_Service_features_section">
          <div className="IT_Service_container">
            <h2 className="IT_Service_section_title IT_Service_center">
              Everything IT teams need in one platform
            </h2>

            <div className="IT_Service_features_grid">
              {features.map((feature, index) => (
                <div key={index} className="IT_Service_feature_card">
                  <div className="IT_Service_feature_header">
                    <div className="IT_Service_feature_icon">
                      {feature.icon}
                    </div>
                    {feature.badge && (
                      <span
                        className="IT_Service_feature_badge"
                        style={{ backgroundColor: feature.badgeColor }}
                      >
                        {feature.badge}
                      </span>
                    )}
                  </div>

                  <h3 className="IT_Service_feature_title">
                    {feature.title}
                  </h3>

                  <p className="IT_Service_feature_description">
                    {feature.description}
                  </p>

                  <div className="IT_Service_feature_arrow">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12h14" />
                      <path d="M12 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Showcase */}
        <section className="IT_Service_showcase_section">
          <div className="IT_Service_container">
            <div className="IT_Service_group_block">
              <div className="IT_Service_group_header">
                <h2 className="IT_Service_group_title">
                  Every project and ticket, organized your way
                </h2>
                <p className="IT_Service_group_subtitle">
                  Track, filter, and drill into every ticket across the
                  department.
                </p>
              </div>
              {renderGroup(trackingGroup, "tracking")}
            </div>

            <div className="IT_Service_group_block">
              <div className="IT_Service_group_header">
                <h2 className="IT_Service_group_title">
                  See your whole team's workload
                </h2>
                <p className="IT_Service_group_subtitle">
                  Visibility and performance, without a status meeting.
                </p>
              </div>
              {renderGroup(teamGroup, "team")}
            </div>

            <div className="IT_Service_group_block">
              <div className="IT_Service_group_header">
                <h2 className="IT_Service_group_title">
                  Stay ahead of deadlines
                </h2>
                <p className="IT_Service_group_subtitle">
                  Sprints, releases, and the reminders that keep them on
                  track.
                </p>
              </div>
              {renderGroup(scheduleGroup, "schedule")}
            </div>

            <div className="IT_Service_group_block">
              <div className="IT_Service_group_header">
                <h2 className="IT_Service_group_title">
                  Office Hub, built in
                </h2>
                <p className="IT_Service_group_subtitle">
                  Leave, attendance, and requests, in the same platform as
                  the work.
                </p>
              </div>
              {renderGroup(officeGroup, "office")}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="IT_Service_benefits_section">
          <div className="IT_Service_container">
            <div className="IT_Service_benefits_wrapper">
              <h2 className="IT_Service_section_title IT_Service_center">
                Why IT teams choose Simplifyte
              </h2>

              <div className="IT_Service_benefits_list">
                {benefits.map((benefit, index) => (
                  <div key={index} className="IT_Service_benefit_item">
                    <CheckCircle2
                      size={24}
                      className="IT_Service_check_icon"
                    />

                    <p>{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="IT_Service_cta_section">
          <div className="IT_Service_container">
            <div className="IT_Service_cta_box">
              <h2 className="IT_Service_cta_title">
                Ready to elevate your IT operations?
              </h2>

              <p className="IT_Service_cta_text">
                Join IT teams who've gained complete visibility and control
                over their service delivery.
              </p>

              <button
                className="IT_Service_cta_button"
                onClick={() =>
                  (window.location.href = "https://app.simplifyte.com")
                }
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </section>
      </div>
      <CustomFooter />
    </>
  );
};

export default ITServicesPage;
